import { Component, Element, Event, EventEmitter, Listen, Method, Prop, State } from '@stencil/core';
import {
  FormComputedProps,
  FormConfig,
  FormErrors,
  FormHandlers,
  FormRenderProps,
  FormState,
  FormTouched,
  FormUtils,
  FormValidator,
  FormValidity,
  FormValues,
  StencilFormEventDetail,
} from './declarations';
import { copyValidityState, getElementValue } from './types';

@Component({
  tag: 'stencil-form',
  // styleUrl: 'form.css'
})
export class StencilForm implements FormConfig {
  @Element() el!: HTMLFormElement;
  private groups: { [key: string]: HTMLElement } = {} as any;
  private inputs: HTMLInputElement[] = [];
  private formId: string = `stencil-form-${formIds++}`;
  private dirty: boolean = false;

  @Method()
  async isFormValid(): Promise<boolean> {
    let isValid = true;

    this.inputs.forEach(inp => {
      isValid = isValid && copyValidityState(inp).valid;
    });

    return isValid;
  }

  @Method()
  async submitForm(): Promise<FormValues> {
    this.submit();
    return this.values;
  }

  @Method()
  async submitFormWithoutValidation(): Promise<FormValues> {
    this.submitWithoutValidation();
    return this.values;
  }

  @Listen('keydown')
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Enter') {
      this.submit();
    }
  }

  @State() isValid: boolean = false;
  @State() isValidating: boolean = false;
  @State() isSubmitting: boolean = false;
  @State() submitCount: number = 0;

  @State() focused: keyof FormValues = null;
  @State() values: FormValues = {} as any;
  @State() touched: FormTouched<FormValues> = {} as any;
  @State() validity: FormValidity<FormValues> = {} as any;
  @State() errors: FormErrors<FormValues> = {} as any;

  @Prop() initialValues: FormValues;
  @Prop() renderer: (props: FormRenderProps<any>, formData?: object) => any = () => null;
  @Prop() validate: FormValidator<FormValues>;


  /** Tells Form to validate the form on each input's onInput event */
  @Prop() validateOnInput?: boolean = true;
  /** Tells Form to validate the form on each input's onBlur event */
  @Prop() validateOnBlur?: boolean = true;
  /** Tell Form if initial form values are valid or not on first render */
  @Prop() isInitialValid?: boolean = true;

  @Event({ eventName: 'formSubmit' }) onFormSubmit: EventEmitter<StencilFormEventDetail>;
  @Event({ eventName: 'formValid' }) onFormValid: EventEmitter;
  @Event({ eventName: 'valueChange' }) onValueChange: EventEmitter<[string, string | File | null | undefined]>;


  componentWillLoad() {
    this.isValid = this.isInitialValid;
    this.values = this.initialValues ?? {};

    for (const field of Object.keys(this.values)) {
      this.touched[field] = false;
      this.errors[field] = this.isInitialValid ? '' : null;
    }
  }

  setSubmitting = (value: boolean) => {
    this.isSubmitting = value;
  };

  submit = () => {
    this.validateAll();
    this.makeAllTouched();
    if (!this.isValid) {
      return;
    }

    this.isSubmitting = true;
    this.submitCount++;
    const { setSubmitting } = this;
    this.onFormSubmit.emit({ values: this.values, actions: { setSubmitting } });
  };

  submitWithoutValidation = () => {
    this.isSubmitting = true;
    this.submitCount++;
    const { setSubmitting } = this;
    this.onFormSubmit.emit({ values: this.values, actions: { setSubmitting } });
  };

  validateAll = () => {
    this.inputs.forEach(inp => {
      this.makeValidation(inp.name, inp);
    });

    this.isValid = Object.values(this.errors).every(x => (x as string) === '');
    if (this.isValid) {
      this.onFormValid.emit();
    }
  };

  makeAllTouched = () => {
    this.inputs.forEach(inp => {
      if (!this.touched[inp.name]) {
        this.touched = { ...this.touched, [inp.name]: true };
      }
    });
  };

  handleReset = () => {
    this.isSubmitting = false;
    this.submitCount = 0;
  };

  makeValidation = (field: string, target: HTMLInputElement | HTMLTextAreaElement) => {
    const validity = copyValidityState(target);
    const { validationMessage } = target;

    let formValidity = { ...this.validity, [field]: validity };
    let formErrors = { ...this.errors, [field]: validationMessage };
    this.validity = formValidity;
    this.errors = formErrors;
  };

  handleInput = (field: string) => async (event: Event) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    let value: any = getElementValue(target);
    if (!value) {
      value = (event as CustomEvent).detail?.value;
    }

    /** Handle file input */
    if (target.type === 'file') {
      value = (target as HTMLInputElement).files?.[0];
    }

    this.values = { ...this.values, [field]: value };

    /** Validate, if user wants to validateOnInput */
    if (this.validateOnInput) {
      this.makeValidation(field, target);
    }

    this.onValueChange.emit([field, value]);
  };

  handleBlur = (field: string) => (event: Event) => {
    if (this.focused) {
      this.focused = null;
    }

    if (!event) {
      if (this.validateOnBlur) {
        setTimeout(() => this.validateAll(), 0);
      }

      return;
    }

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    let value: any = getElementValue(target);
    if (!value) {
      value = (event as CustomEvent).detail?.value;
    }

    /** Handle file input */
    if (target.type === 'file') {
      value = (target as HTMLInputElement).files?.[0];
    }

    this.values = { ...this.values, [field]: value };
    if (this.validateOnBlur) {
      setTimeout(() => this.validateAll(), 0);
    }
  };

  handleFocus = (field: string) => () => {
    this.focused = field;
    if (!this.touched[field]) this.touched = { ...this.touched, [field]: true };
  };

  private composedState = (): FormState<FormValues> => {
    const { focused, values, errors, validity, touched, isValidating, isSubmitting, submitCount } = this;
    return { focused, values, errors, validity, touched, isValidating, isSubmitting, submitCount };
  };

  private composedHandlers = (): FormHandlers<FormValues> => {
    const { handleReset, handleInput, handleFocus, handleBlur } = this;
    return { handleReset, handleInput, handleFocus, handleBlur };
  };

  private computeProps = () => {
    this.dirty = !Object.values(this.touched).every(x => !x);
    this.isValid = Object.values(this.validity).every(x => {
      return (x as any)?.valid!;
    });
  };

  private composedComputedProps = (): FormComputedProps<FormValues> => {
    this.computeProps();
    const { dirty, isValid, initialValues } = this;
    return { dirty, isValid, initialValues };
  };

  private composedUtils = (): FormUtils<FormValues, keyof FormValues> => {
    const groupProps = (field: keyof FormValues) => {
      return {
        'data-for': field,
        'class': {
          'input-group': true,
          'was-touched': this.touched[field],
          'has-focus': this.focused === field,
          'has-value': typeof this.values[field] === 'string' ? !!this.values[field] : typeof this.values[field] === 'number' ? typeof this.values[field] !== null : false,
          'has-error': this.validity[field] && !this.validity[field].valid,
        },
        'ref': el => (this.groups = { ...this.groups, [field]: el }),
      };
    };

    const inputProps = (field: keyof FormValues) => ({
      name: field,
      ref: (el: HTMLInputElement) => (this.inputs = [...new Set([...this.inputs, el])]),
      unRef: (el: HTMLInputElement, name: string) => {
        this.inputs = this.inputs.filter(item => item !== el);
        delete this.validity[name];
        delete this.errors[name];
      },
      type: 'text',
      onInput: this.handleInput(field as string),
      onBlur: this.handleBlur(field as string),
      onFocus: this.handleFocus(field as string),
      id: `${this.formId}-input-${field as string}`,
      value: this.values[field] ?? '',
      error: this.validity[field] && !this.validity[field].valid,
      wasTouched: this.touched[field],
    });

    const radioProps = (field: keyof FormValues, value: string) => ({
      ...inputProps(field),
      type: 'radio',
      id: `${this.formId}-input-${field as string}--radio-${value}`,
      value: value,
      checked: this.values[field] === value,
    });

    const checkboxProps = (field: keyof FormValues) => ({
      ...inputProps(field),
      type: 'checkbox',
      value: null,
      error: this.validity[field] && !this.validity[field].valid,
      checked: !!this.values[field],
    });

    const selectProps = (field: keyof FormValues) => ({
      name: field,
      id: `${this.formId}-input-${field as string}`,
      value: this.values[field],
      ref: inputProps(field).ref,
      unRef: inputProps(field).unRef,
      onChange: async (event: Event) => {
        await this.handleInput(field as string)(event);
      },
      onBlur: this.handleBlur(field as string),
      onFocus: this.handleFocus(field as string),
      error: this.validity[field] && !this.validity[field].valid,
      wasTouched: this.touched[field],
    });

    const labelProps = (field: keyof FormValues, value?: string) => ({
      htmlFor: !value ? `${this.formId}-input-${field as string}` : `${this.formId}-input-${field as string}--radio-${value}`,
    });

    return { groupProps, inputProps, selectProps, checkboxProps, radioProps, labelProps };
  };

  render() {
    const state: FormState<FormValues> = this.composedState();
    const handlers: FormHandlers<FormValues> = this.composedHandlers();
    const computedProps: FormComputedProps<FormValues> = this.composedComputedProps();
    const utils: FormUtils<FormValues, keyof FormValues> = this.composedUtils();

    const renderProps: FormRenderProps<any> = { ...state, ...handlers, ...computedProps, ...utils };
    return this.renderer(renderProps);
  }
}

let formIds: number = 0;
